import { ChangeDetectionStrategy, Component, Directive, Input } from '@angular/core';
import {
  CdkFooterRow,
  CdkFooterRowDef,
  CdkHeaderRow,
  CdkHeaderRowDef,
  CdkRow,
  CdkRowDef,
  CdkCellOutlet,
  DataRowOutlet,
  HeaderRowOutlet,
  FooterRowOutlet,
} from '@angular/cdk/table';

@Directive({
  selector: '[elRowOutlet]',
  providers: [{ provide: DataRowOutlet, useExisting: ElDataRowOutletDirective }],
})
export class ElDataRowOutletDirective extends DataRowOutlet { }

@Directive({
  selector: '[elHeaderRowOutlet]',
  providers: [{ provide: HeaderRowOutlet, useExisting: ElHeaderRowOutletDirective }],
})
export class ElHeaderRowOutletDirective extends HeaderRowOutlet { }

@Directive({
  selector: '[elFooterRowOutlet]',
  providers: [{ provide: FooterRowOutlet, useExisting: ElFooterRowOutletDirective }],
})
export class ElFooterRowOutletDirective extends FooterRowOutlet { }

@Directive({
  selector: '[elCellOutlet]',
  providers: [{ provide: CdkCellOutlet, useExisting: ElCellOutletDirective }],
})
export class ElCellOutletDirective extends CdkCellOutlet { }

/**
 * Header row definition for the el-table.
 * Captures the header row's template and other header properties such as the columns to display.
 */
@Directive({
  selector: '[elHeaderRowDef]',
  providers: [{ provide: CdkHeaderRowDef, useExisting: ElHeaderRowDefDirective }],
})
export class ElHeaderRowDefDirective extends CdkHeaderRowDef {
  @Input('elHeaderRowDef') columns: Iterable<string>;
  @Input('elHeaderRowDefSticky') sticky: boolean;
}

/**
 * Footer row definition for the el-table.
 * Captures the footer row's template and other footer properties such as the columns to display.
 */
@Directive({
  selector: '[elFooterRowDef]',
  providers: [{ provide: CdkFooterRowDef, useExisting: ElFooterRowDefDirective }],
})
export class ElFooterRowDefDirective extends CdkFooterRowDef {
  @Input('elFooterRowDef') columns: Iterable<string>;
  @Input('elFooterRowDefSticky') sticky: boolean;
}

/**
 * Data row definition for the el-table.
 * Captures the data row's template and other properties such as the columns to display and
 * a when predicate that describes when this row should be used.
 */
@Directive({
  selector: '[elRowDef]',
  providers: [{ provide: CdkRowDef, useExisting: ElRowDefDirective }],
})
export class ElRowDefDirective<T> extends CdkRowDef<T> {
  @Input('elRowDefColumns') columns: Iterable<string>;
  @Input('elRowDefWhen') when: (index: number, rowData: T) => boolean;
}

/** Footer template container that contains the cell outlet. Adds the right class and role. */
@Component({
  selector: 'el-header-row, tr[elHeaderRow]',
  template: `
    <ng-container elCellOutlet></ng-container>`,
  host: {
    'class': 'el-header-row',
    'role': 'row',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: CdkHeaderRow, useExisting: ElHeaderRowComponent }],
})
export class ElHeaderRowComponent extends CdkHeaderRow {
}

/** Footer template container that contains the cell outlet. Adds the right class and role. */
@Component({
  selector: 'el-footer-row, tr[elFooterRow]',
  template: `
    <ng-container elCellOutlet></ng-container>`,
  host: {
    'class': 'el-footer-row',
    'role': 'row',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: CdkFooterRow, useExisting: ElFooterRowComponent }],
})
export class ElFooterRowComponent extends CdkFooterRow {
}

/** Data row template container that contains the cell outlet. Adds the right class and role. */
@Component({
  selector: 'el-row, tr[elRow]',
  template: `
    <ng-container elCellOutlet></ng-container>`,
  host: {
    'class': 'el-row',
    'role': 'row',
  },
  providers: [{ provide: CdkRow, useExisting: ElRowComponent }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ElRowComponent extends CdkRow {
}
